import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero, Section } from "@/components/SharedUI";
import EngagementTimeline from "@/components/EngagementTimeline";

const tiers = [
  ["Toolkit Starter", "From $25,000 ex GST", "For early-stage projects needing governance setup.",
    ["Diagnostic", "Templates", "Registers", "UNDRIP matrix", "30/60/90 day roadmap"], false],
  ["Governance Architecture Pack", "From $65,000 ex GST", "For government project teams preparing for implementation.",
    ["Current-state review", "Authority map", "Decision-rights matrix", "Risk model", "Executive evidence pack"], false],
  ["Protected Pilot", "From $125,000 ex GST", "For controlled agency or project pilots.",
    ["Toolkit", "Demo environment", "Consent workflows", "ICIP and IDS registers", "Final pilot report"], true],
  ["Enterprise / Government Licence", "By proposal", "For whole-of-program or agency-wide governance architecture.",
    ["Platform licence", "Toolkit licence", "Training", "Governance onboarding", "Assurance reporting"], false],
];

export default function Pricing() {
  return (
    <>
      <PageHero
        eyebrow="Pricing"
        title="Priced by governance complexity, cultural authority requirements, data sensitivity, implementation risk and licensing scope."
        desc="Use from-pricing publicly and quote by proposal after discovery. Detailed product pricing is kept private until the client's scope, risk and delivery environment are understood."
      />
      <section className="border-b border-stone-200">
        <Section>
          <div className="grid gap-px bg-stone-200 border border-stone-200 md:grid-cols-2 lg:grid-cols-4">
            {tiers.map(([title, price, desc, bullets, highlight]) => (
              <div
                key={title}
                data-testid={`pricing-tier-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`bg-[#FAFAF9] p-8 flex flex-col ${highlight ? "ring-2 ring-[#9C4221] ring-inset relative" : ""}`}
              >
                {highlight && (
                  <div className="inline-flex w-fit text-[10px] tracking-[0.24em] uppercase font-bold text-[#9C4221] mb-4">
                    Most chosen
                  </div>
                )}
                <h3 className="font-display text-2xl tracking-tight font-bold text-stone-900">{title}</h3>
                <div className="mt-5 font-display text-2xl tracking-tight font-bold text-[#9C4221]">{price}</div>
                <p className="mt-3 text-sm text-stone-600 leading-relaxed min-h-20">{desc}</p>
                <ul className="mt-5 grid gap-3 flex-1">
                  {bullets.map((b) => (
                    <li key={b} className="flex gap-2 text-sm text-stone-700">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#9C4221] shrink-0" strokeWidth={2.2} />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Engagement timeline */}
      <EngagementTimeline />

      {/* Product Catalogue CTA */}
      <section className="border-b border-stone-200 bg-stone-50">
        <Section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
              For board, executive and procurement teams
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight font-bold text-stone-900 leading-tight">
              Tailored engagement — capability statement and product catalogue on request.
            </h2>
            <p className="mt-4 text-stone-700 leading-relaxed max-w-xl">
              Detailed product tiers, deliverables and commercial terms are shared under Background IP through direct engagement. Request a discovery call to receive a scoped capability statement.
            </p>
          </div>
          <div className="lg:col-span-5 lg:justify-self-end flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              data-testid="pricing-catalogue-cta"
              className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-4 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
            >
              Request catalogue <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Section>
      </section>

      {/* Readiness CTA */}
      <section className="border-b border-stone-200">
        <Section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
              Not sure which tier?
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight font-bold text-stone-900 leading-tight">
              Take the two-minute Governance Readiness self-assessment.
            </h2>
            <p className="mt-4 text-stone-700 leading-relaxed max-w-xl">
              Six questions across authority, consent, data sovereignty and audit evidence. Get a banded read and a recommended tier — confidential, runs in your browser, no email required.
            </p>
          </div>
          <div className="lg:col-span-5 lg:justify-self-end">
            <Link
              to="/readiness"
              data-testid="pricing-readiness-cta"
              className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-4 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
            >
              Take the Readiness Assessment <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Section>
      </section>
    </>
  );
}
